// Bilingual copy for Wen Jie Studio. Keep concise, premium, no hype.
// Proper nouns (project code names, tool names) remain in Latin script.

export const translations = {
  en: {
    lang: { switchTo: "中" },
    nav: {
      home: "Home",
      about: "About",
      services: "Services",
      work: "Work",
      process: "Process",
      tools: "Tools",
      contact: "Contact",
      cta: "Let’s Chat",
    },
    hero: {
      pill: "A quiet digital studio · Design. Build. Launch.",
      headline_a: "Ideas, shaped into",
      headline_b: "premium digital",
      headline_c: "experiences.",
      sub: "Wen Jie Studio designs and builds websites, landing pages, and product experiences with the calm confidence of a serious digital practice — for founders, coaches, agencies, and small businesses who care about craft.",
      cta_primary: "Let’s Chat",
      cta_secondary: "View My Work",
      scroll: "Scroll to explore",
    },
    about: {
      label: "About",
      heading_a: "A studio built",
      heading_b: " on ",
      heading_accent: "calm craft",
      heading_c: " — not noise.",
      quote: [
        "I care about clarity.",
        "I care about details.",
        "I care about outcomes",
        "that actually move the needle.",
      ],
      signature: "Wen Jie",
      role: "Founder, Wen Jie Studio",
      story_1_h: "I didn’t start in design.",
      story_1_p: "I started in finance, where I learned to value clarity, structure, and thoughtful decision-making.",
      story_2_h: "Over time, curiosity pulled me toward product design, websites, AI, and automation —",
      story_2_p: "not because they were trendy, but because they solve real business problems.",
      story_3_h: "Today, Wen Jie Studio helps founders, coaches, consultants, real estate professionals, and growing businesses turn ideas into premium digital experiences.",
      story_3_p: "WJ Studio, officially Wen Jie Studio, is a Malaysia-based AI digital studio building premium websites, landing pages, digital products and AI-powered digital experiences.",
      closing: [
        "Every project is built with intention.",
        "Less noise. More clarity.",
        "Built to launch. Built to last.",
      ],
      cards: {
        finance: {
          title: "Finance Background",
          body: "Business-first thinking and analytical mindset.",
        },
        ai_design: {
          title: "AI + Design Focused",
          body: "Modern tools creating smarter digital experiences.",
        },
        impact: {
          title: "Built for Impact",
          body: "Websites and systems that help businesses grow.",
        },
      },
    },
    services: {
      label: "Services",
      heading: "What we design, build, and ship.",
      subheading: "Every engagement is bespoke. These are the shapes it usually takes.",
      items: {
        "website-design": {
          title: "Website Design",
          description: "Distinctive brand websites engineered for clarity, speed, and quiet confidence. Built to convert without shouting.",
          features: ["Brand-led visuals", "Custom typography", "Responsive across devices"],
        },
        "landing-pages": {
          title: "Landing Pages",
          description: "Focused, high-intent pages for launches, funnels, and campaigns. Every pixel is earned.",
          features: ["Conversion-first layout", "A/B ready", "Motion refinements"],
        },
        "ui-ux": {
          title: "UI / UX Design",
          description: "Product interfaces that feel effortless. Interaction flows shaped around real user behaviour, not templates.",
          features: ["User research", "Design systems", "Prototype to production"],
        },
        "ai-products": {
          title: "AI-Assisted Products",
          description: "Thoughtful integrations of modern intelligence — where it earns its place in the interface, and never for its own sake.",
          features: ["Content workflows", "Assistive UX", "Model-agnostic"],
        },
        "automation": {
          title: "Automation-Ready Systems",
          description: "Websites wired to the tools that run your business. Fewer manual steps, more time spent on the work itself.",
          features: ["CRM & CMS", "Zapier / n8n flows", "Analytics baked in"],
        },
        "strategy": {
          title: "Business Website Strategy",
          description: "Clarity before pixels. Positioning, messaging, and information architecture that make design decisions obvious.",
          features: ["Positioning audit", "Sitemap & IA", "Content direction"],
        },
      },
    },
    work: {
      label: "Selected Work",
      heading: "Concept projects, shaped like the real thing.",
      subheading: "Portfolio demonstrations — not commercial client work. Designed so you can imagine yours in their place.",
      badge: "Studio Concept",
      col: { objective: "Objective", approach: "Approach", features: "Key features", tech: "Tech" },
      items: {
        "atelier-modern-business": {
          category: "Modern Business Website",
          title: "Atelier — A quiet business website concept",
          objective: "Reimagine the corporate website as a calm, editorial experience — trust through restraint.",
          approach: "Editorial grid, generous white space, subtle motion on scroll, and a hero anchored in typography.",
          features: ["Editorial hero", "Case study module", "Careers page pattern"],
        },
        "lumen-saas-dashboard": {
          category: "SaaS Dashboard",
          title: "Lumen — Analytics dashboard concept",
          objective: "Show how a data product can feel human — dense, but calm; expressive, without noise.",
          approach: "Charcoal canvas, single accent, micro-interactions on data cells, keyboard-first navigation.",
          features: ["Command palette", "Real-time charts", "Collaborative comments"],
        },
        "north-real-estate": {
          category: "Real Estate Website",
          title: "North — Boutique real estate concept",
          objective: "Position luxury inventory through storytelling rather than listings and lead-forms.",
          approach: "Full-bleed cinematography, quiet numbers, private-by-request flows, and neighborhood editorial.",
          features: ["Cinematic hero", "Property gallery", "Private inquiry"],
        },
        "saison-restaurant": {
          category: "Restaurant Website",
          title: "Saison — Fine-dining restaurant concept",
          objective: "Translate a chef’s intent into a website that behaves like a menu — patient, tactile, considered.",
          approach: "Serif accents, dish-first imagery, quiet booking flow, mobile-first with tactile hover states.",
          features: ["Tasting menu view", "Reservation flow", "Press & story"],
        },
        "arc-coach-consultant": {
          category: "Coach & Consultant",
          title: "Arc — Executive coach concept",
          objective: "Give a solo practitioner a website that reads like a book’s inside flap — clear, singular, memorable.",
          approach: "Editorial spread, credibility built through case notes and long-form, minimal booking friction.",
          features: ["Booking module", "Long-form essays", "Testimonials pattern"],
        },
        "field-landing-suite": {
          category: "Landing Page System",
          title: "Field — Launch landing suite concept",
          objective: "A modular landing system for startups shipping monthly — same voice, different pages, no drift.",
          approach: "Design tokens per campaign, shared motion library, analytics wired from day one.",
          features: ["Design tokens", "Reusable blocks", "Split testing"],
        },
      },
    },
    process: {
      label: "Process",
      heading_a: "A journey, from",
      heading_accent: "idea to launch",
      heading_b: " — and beyond.",
      steps: {
        discover: { title: "Discover", body: "We start with your business, not your website. Positioning, audience, and the outcome that actually matters." },
        plan: { title: "Plan", body: "Sitemap, content direction, and a clear scope. No surprises later — the plan is the contract." },
        design: { title: "Design", body: "Typography, hierarchy, motion — designed to feel considered, quietly premium, unmistakably yours." },
        build: { title: "Build", body: "Fast, accessible, and wired to your stack. Everything a modern site should be, without the bloat." },
        launch: { title: "Launch", body: "We ship carefully — DNS, analytics, and a walkthrough so your team owns the site from day one." },
        improve: { title: "Improve", body: "The website is a product. We iterate — small refinements, new sections, better performance over time." },
      },
    },
    tools: {
      label: "Tools",
      heading: "A quiet, considered stack.",
      subheading: "The tools we reach for across AI, design, development, and automation — chosen because they earn their place.",
      categories: { ai: "AI", design: "Design", dev: "Development", automation: "Automation" },
    },
    contact: {
      label: "Contact",
      heading_a: "Let’s start a ",
      heading_accent: "quiet conversation",
      heading_b: ".",
      intro: "Choose the way you’d like to connect. Whether it’s LinkedIn, Instagram, Facebook, or email — every conversation starts with your idea.",
      currently_label: "Currently",
      currently_body: "Currently accepting a limited number of projects.",
      cta_follow: "Follow the Studio",
      email_inactive: "Coming after launch",
      channels: {
        linkedin: { label: "LinkedIn", tagline: "Professional inquiries", detail: "Let’s talk about a project or an engagement." },
        instagram: { label: "Instagram", tagline: "Quick messages", detail: "For casual questions and studio updates." },
        facebook: { label: "Facebook", tagline: "Casual conversation", detail: "Say hi — happy to chat." },
        email: { label: "Email", tagline: "Detailed project discussions", detail: "Briefs, scoping, and considered replies." },
      },
    },
    footer: {
      tagline: "Design. Build. Launch. — a quiet digital studio helping founders and small teams turn ideas into premium websites and digital products.",
      rights: "All Rights Reserved.",
      crafted: "Crafted with care.",
    },
  },

  // --- Simplified Chinese ---
  zh: {
    lang: { switchTo: "EN" },
    nav: {
      home: "首页",
      about: "关于",
      services: "服务",
      work: "作品",
      process: "流程",
      tools: "工具",
      contact: "联系",
      cta: "与我们聊聊",
    },
    hero: {
      pill: "低调而专注的数字工作室 · 设计 · 构建 · 上线",
      headline_a: "把想法，塑造成",
      headline_b: "高品质的数字",
      headline_c: "体验。",
      sub: "Wen Jie Studio 以严谨而克制的态度，为创始人、教练、代理机构与注重品质的小型企业，设计并构建网站、着陆页与产品体验。",
      cta_primary: "与我们聊聊",
      cta_secondary: "查看作品",
      scroll: "向下滚动探索",
    },
    about: {
      label: "关于",
      heading_a: "一家",
      heading_b: "以",
      heading_accent: "沉静与专注",
      heading_c: "为核心的工作室——从不喧哗。",
      quote: [
        "我在意「清晰」。",
        "我在意「细节」。",
        "我在意「真正",
        "能推动业务的结果」。",
      ],
      signature: "Wen Jie",
      role: "创办人 · Wen Jie Studio",
      story_1_h: "我并非从设计出发。",
      story_1_p: "我毕业于金融专业，学会了如何珍视清晰、结构与深思熟虑的判断。",
      story_2_h: "后来，好奇心把我带向产品设计、网站、AI 与自动化——",
      story_2_p: "并不是因为它们「时髦」，而是因为它们能真正解决商业问题。",
      story_3_h: "如今，Wen Jie Studio 帮助创始人、教练、顾问、房地产从业者与成长型企业，将想法打磨为高品质的数字体验。",
      story_3_p: "WJ Studio（正式名称 Wen Jie Studio）是一间来自马来西亚的 AI 数字工作室，专注打造高品质的网站、落地页、数字产品，以及由 AI 驱动的数字体验。",
      closing: [
        "每一个项目都有明确的意图。",
        "更少的噪音，更多的清晰。",
        "为「上线」而生。为「长久」而建。",
      ],
      cards: {
        finance: { title: "金融背景", body: "以商业为先的思维与分析型头脑。" },
        ai_design: { title: "AI + 设计", body: "以现代工具创造更聪明的数字体验。" },
        impact: { title: "为影响力而建", body: "帮助企业成长的网站与系统。" },
      },
    },
    services: {
      label: "服务",
      heading: "我们设计、构建，并把它送上线。",
      subheading: "每一次合作都是量身定制的。以下是它常见的形态。",
      items: {
        "website-design": {
          title: "品牌网站设计",
          description: "为「清晰、速度与沉稳质感」而设计的品牌网站，能在不喧闹的前提下带来转化。",
          features: ["以品牌为核心的视觉", "定制字体系统", "全设备响应"],
        },
        "landing-pages": {
          title: "着陆页设计",
          description: "为发布、漏斗与营销活动量身打造的高意图页面。每一个像素都有理由。",
          features: ["以转化为先的布局", "支持 A/B 实验", "细腻的动效"],
        },
        "ui-ux": {
          title: "UI / UX 设计",
          description: "让产品界面「用起来毫不费力」——以真实用户行为，而非模板，来塑造交互流程。",
          features: ["用户研究", "设计系统", "从原型到上线"],
        },
        "ai-products": {
          title: "AI 辅助的数字产品",
          description: "在合适的位置引入现代智能——只在它真正带来价值时才出现。",
          features: ["内容工作流", "辅助型 UX", "模型无关"],
        },
        "automation": {
          title: "面向自动化的系统",
          description: "把网站与你正在使用的工具打通。更少的手动步骤，更多的时间留给真正的工作。",
          features: ["CRM 与 CMS", "Zapier / n8n 流程", "数据分析内建"],
        },
        "strategy": {
          title: "商业网站策略",
          description: "先厘清，再动手。定位、信息与结构，让设计决策一目了然。",
          features: ["定位梳理", "站点结构", "内容方向"],
        },
      },
    },
    work: {
      label: "精选作品",
      heading: "如同真实项目一般打磨的概念作品。",
      subheading: "这些是作品集展示，并非商业客户项目——你可以把它们，想象成你自己的项目。",
      badge: "工作室概念",
      col: { objective: "目标", approach: "方式", features: "关键功能", tech: "技术栈" },
      items: {
        "atelier-modern-business": {
          category: "现代企业网站",
          title: "Atelier — 一个安静的企业网站概念",
          objective: "把企业官网重新想象成一份从容、编辑感十足的作品——以克制赢得信任。",
          approach: "编辑式栅格、大量留白、克制的滚动动效，以字体为锚点的英雄区。",
          features: ["编辑感英雄区", "案例展示模块", "招聘页面模式"],
        },
        "lumen-saas-dashboard": {
          category: "SaaS 仪表盘",
          title: "Lumen — 数据分析仪表盘概念",
          objective: "呈现「数据产品也可以是温柔的」——密而不乱、有表达但不喧闹。",
          approach: "深灰主色、单一强调色、数据单元格的微交互，以键盘为先的导航。",
          features: ["命令面板", "实时图表", "协作评论"],
        },
        "north-real-estate": {
          category: "房地产网站",
          title: "North — 精品房地产概念",
          objective: "用叙事而非清单与表单，来呈现高端房产。",
          approach: "全屏影像、克制的数字、按需私询流程，以及社区编辑内容。",
          features: ["影院级英雄区", "房产图库", "私密询问"],
        },
        "saison-restaurant": {
          category: "餐厅网站",
          title: "Saison — 高端餐厅概念",
          objective: "把主厨的意图，转化成一份如「菜单」般耐读、可触、经思考的网页。",
          approach: "衬线点缀、以菜为先的影像、安静的预订流程、以移动端为先的触感设计。",
          features: ["品尝菜单视图", "预订流程", "媒体与故事"],
        },
        "arc-coach-consultant": {
          category: "教练与顾问",
          title: "Arc — 高管教练概念",
          objective: "为独立执业者打造一份「像书的扉页」般的网站——清晰、独特、令人记住。",
          approach: "编辑式版面、以案例笔记与长文构建信任、极低的预约摩擦。",
          features: ["预约模块", "长文随笔", "证言模式"],
        },
        "field-landing-suite": {
          category: "着陆页系统",
          title: "Field — 发布着陆页组合概念",
          objective: "为每月都在发布的初创公司打造的模块化着陆系统——同一种声音，不同的页面，永不走样。",
          approach: "按活动配置的设计代币、共享动效库，数据分析从第一天就到位。",
          features: ["设计代币", "可复用模块", "分流测试"],
        },
      },
    },
    process: {
      label: "流程",
      heading_a: "从",
      heading_accent: "想法到上线",
      heading_b: "——以及此后的每一步。",
      steps: {
        discover: { title: "发现", body: "从你的业务而非网站出发：定位、受众，以及真正重要的成果。" },
        plan: { title: "规划", body: "站点结构、内容方向与清晰的范围——之后不会出现意外，计划即约定。" },
        design: { title: "设计", body: "字体、层级、动效——都为了让它显得经过思考、安静高级，且独属于你。" },
        build: { title: "构建", body: "快速、可访问，且与你的工具打通——一个现代网站该有的一切，没有多余的负担。" },
        launch: { title: "上线", body: "我们谨慎地上线——DNS、数据分析、以及一次走查，让团队从第一天就能自如接手。" },
        improve: { title: "改进", body: "网站是一款产品——我们持续打磨：小修改、新板块、更好的表现。" },
      },
    },
    tools: {
      label: "工具",
      heading: "一份安静而经过挑选的技术栈。",
      subheading: "我们在 AI、设计、开发与自动化中反复使用的工具——它们都凭实力赢得了自己的位置。",
      categories: { ai: "AI", design: "设计", dev: "开发", automation: "自动化" },
    },
    contact: {
      label: "联系",
      heading_a: "让我们开启一场 ",
      heading_accent: "安静的对话",
      heading_b: "。",
      intro: "选择你最习惯的方式与我们联系。无论是 LinkedIn、Instagram、Facebook 还是邮件——每一次交流，都从你的想法开始。",
      currently_label: "当前状态",
      currently_body: "我们正接受少量经过慎重挑选的项目。",
      cta_follow: "关注工作室",
      email_inactive: "上线后开放",
      channels: {
        linkedin: { label: "LinkedIn", tagline: "商业合作洽谈", detail: "聊聊项目或长期合作。" },
        instagram: { label: "Instagram", tagline: "轻松沟通", detail: "日常提问，或了解工作室动态。" },
        facebook: { label: "Facebook", tagline: "随意聊聊", detail: "打个招呼，我们随时都在。" },
        email: { label: "邮件", tagline: "深入的项目讨论", detail: "简报、范围、以及经过思考的回复。" },
      },
    },
    footer: {
      tagline: "设计 · 构建 · 上线 — 一家安静的数字工作室，帮助创始人与小团队把想法打磨为高品质的网站与数字产品。",
      rights: "保留所有权利。",
      crafted: "以匠心打造。",
    },
  },
};

export const LANG_ORDER = ["en", "zh"];
